// @flow
import { combineActions, handleActions } from 'redux-actions';

import {
  refreshAccountsMfaFormSubmitResponse,
  upsertAccountsSuccess,
  upsertAccountsFailure,
} from 'data/accounts/actions';
import { getQcsAggregationError } from 'data/aggErrors/errors';
import { getInstitution, getInstitutionResponse } from 'data/institutions/actions';
import {
  addInstitutionLoginAndAccountsResponse,
  institutionLoginSetCredentialsBlob,
  refreshAccountsCredentialsProvided,
} from 'data/institutionLogins/actions';

import { dangerouslyUseFeatureFlags } from 'utils/featureFlags/selectors';

import * as actions from './actions';
import { mkInstitutionLoginUpserterStore } from './types';

export const reducer = handleActions({

  [actions.accountDiscoverySetup]: (_, { payload : { institutionLogin, mfaChallenge } }) =>
    mkInstitutionLoginUpserterStore({ institutionLogin, mfaChallenge }),

  [actions.accountDiscoveryReset]: () => mkInstitutionLoginUpserterStore(),

  // TODO: Need to fix this one!
  [actions.accountDiscoveryResetToCredentialState]: (state, { payload : data }) => (
    state.merge({
      ...data,

      mfaChallenge: null,
      partnerAuthUris: null,

      accountDiscoveryData: null,
    })
  ),

  [actions.institutionSelected]: (state, { payload : institutionId, asyncDispatch }) => {
    if (asyncDispatch) {
      asyncDispatch(getInstitution({ institutionId }));
    }
    const institutionLogin = state.institutionLogin ? state.institutionLogin.set('institutionId', institutionId) : null;
    return state.merge({ institutionId, institutionLogin });
  },

  [actions.accountDiscoveryCredentialsFormSubmit]: (state) => (
    state.merge({
      error: false,
      isSubmitting: true,
    })
  ),

  [actions.accountDiscoveryCredentialsFormSubmitResponse]: {
    next(state, { payload : institutionLogin }) {
      return state.merge({
        institutionLogin,

        isPolling: true,
        isSubmitting: false,
      });
    },
    throw(state, _error) {
      return state.merge({isSubmitting: false });
    },
  },

  [actions.accountDiscoveryMfaRequired]: (state, { payload: data }) => state.merge({
    mfaChallenge: data,

    isPolling: false,
    isSubmitting: false,
  }),

  [actions.accountDiscoveryMfaFormSubmit]: (state) =>
    state.merge({
      error: null,

      isSubmitting: true,
    }),

  [actions.accountDiscoveryMfaFormSubmitResponse]: {
    next(state) {
      return state.merge({
        mfaChallenge: null,

        isPolling: true,
        isSubmitting: false,
      });
    },
    throw(state, _error) {
      return state.merge({
        mfaChallenge: null,

        isSubmitting: false,
      });
    },
  },

  [actions.getPartnerAuthUris]: (state) => (
    state.merge({
      error: false,
    })
  ),

  [actions.getPartnerAuthUrisSuccess]: (state, { payload: partnerAuthUris }) =>
    state.merge({
      partnerAuthUris,
    }),

  [actions.getPartnerAuthUrisFailure]: (state) => state.merge({
    // todo: how should we notify user of failure
  }),

  [actions.accountDiscoverySuccessWithAccounts]: (state, { payload: accountDiscoveryData, asyncDispatch }) => {
    const newState = state.merge({
      accountDiscoveryData,

      isAuthenticationDone: true,
      isPolling: false,
      isSubmitting: false,
    });

    if (accountDiscoveryData.credentialsBlob) {
      asyncDispatch(
        institutionLoginSetCredentialsBlob({
          institutionLoginId: accountDiscoveryData.institutionLoginId,
          credentialsBlob: accountDiscoveryData.credentialsBlob,
        })
      );
    }

    return newState;
  },

  [actions.accountDiscoverySuccess]: (state) =>
    state.merge({
      isAuthenticationDone: true,
      isPolling: false,
      isSubmitting: false,
    }),
  [actions.accountDiscoveryFailure]: (state, { payload }) => {
    const { adStatus, institutionLogin } = payload;
    let error = null;
    if (adStatus.status === 'AGGREGATOR_IN_ERROR') {
      const {aggStatus, cpAggStatusCode} = adStatus.aggregators.get(0);
      error = getQcsAggregationError(institutionLogin, aggStatus, cpAggStatusCode, dangerouslyUseFeatureFlags());
    }
    return state.merge({
      error,

      isPolling: false,
      isSubmitting: false,
    });
  },
  [actions.accountDiscoveryError]: (state, { payload }) => {
    const { institutionLogin } = payload;

    // todo: how should we pass on message from error object?

    const aggStatus = "CLIENT_ERROR";
    const cpAggStatusCode = 'client-error';

    const qcsAggregationError = getQcsAggregationError(institutionLogin, aggStatus, cpAggStatusCode, dangerouslyUseFeatureFlags());
    return state.merge({
      error: qcsAggregationError,

      isPolling: false,
      isSubmitting: false,
    });
  },

  [actions.accountDiscoveryStopped]: (state) =>
    state.merge({
      isPolling: false,
      isSubmitting: false,
    }),

  [actions.accountDiscoveryPlaidItemUpdateSuccess]: (state, { payload: { institutionLogin, accountDiscoveryData }, asyncDispatch}) => {

    // when item is successful, lets go ahead and fire off the action to get institution (required in later steps)
    asyncDispatch(getInstitution({ institutionId: institutionLogin.institutionId }));

    const newState = state.merge({
      institutionLogin,
      accountDiscoveryData,

      isAuthenticationDone: true,
    });

    return newState;
  },

  [actions.discoverInstitutionLoginWithAccounts]: (state, { payload: { institutionLogin }, asyncDispatch}) => {

    // when discovering, lets go ahead and fire off the action to get institution (required in later steps if we don't already have it)
    asyncDispatch(getInstitution({ institutionId: institutionLogin.cpInstitutionId, idType: "plaidId" } ));

    return state.merge({
      institutionLogin,
      isSubmitting: true,
    });
  },

  [actions.rediscoverInstitutionLogin]: (state, { payload: { institutionLogin }}) => (
    state.merge({
      institutionLogin,
      isSubmitting: true,
    })
  ),

  [combineActions(
    actions.discoverInstitutionLoginWithAccountsResponse,
    actions.rediscoverInstitutionLoginResponse,
  )]: {
    next(state, { payload: { institutionLogin, accountDiscoveryData }}) {

      // If we haven't got an institutionId in the institutionLogin (we won't if new login), then
      // use one in current state object (may be there if get-institution call finishes  before
      // this response.
      //
      const mergedInstitutionLogin = !institutionLogin.institutionId
        ? institutionLogin.set('institutionId', state.institutionLogin.institutionId)
        : institutionLogin;

      return state.merge({
        institutionLogin: mergedInstitutionLogin,
        accountDiscoveryData,

        isAuthenticationDone: true,
        isSubmitting: false,
      });
    },
    throw(state, _error) {
      return state.merge({
        isSubmitting: false,
      });
    },
  },

  [addInstitutionLoginAndAccountsResponse]: {
    next(state, { payload: { institutionLogin }}) {
      return state.merge({
        institutionLogin,

        isAccountUpsertingDone: true,
        isSubmitting: false,
      });
    },
    throw(state, _error) {
      return state.merge({
        isSubmitting: false,
      });
    },
  },

  [getInstitutionResponse]: {
    next(state, { payload: institution }) {
      // If we dont yet have an institutionId but an institition has beeen setup that includes a cpInstitutionId,
      // then we should check if incoming institution matches the cpInstitutionId. If it does, then we need to
      // set institutionId and also login.institutionId.
      //
      // An example of where this occurs is when we are adding a Plaid item. When we initially discover, we only
      // have the plaid institution ID (cpInstitutionId).
      //
      // TODO: Should work with any cpInstitutionId, not just plaid
      //
      if (!state.institutionId
        && state.institutionLogin
        && institution.plaid
        && state.institutionLogin.cpInstitutionId === institution.plaid.id)
      {
        return state.merge({
          institutionId: institution.id,
          institutionLogin: state.institutionLogin.set('institutionId', institution.id),
        });
      }
      return state;
    },
  },

  [actions.getPlaidPublicTokenResponse]: {
    next(state, { payload: { publicToken }}) {
      return state.merge({
        publicToken,
      });
    },
    throw(state, _error) {
      return state.merge({
        // TODO: Handle Error
      });
    },
  },

  [refreshAccountsCredentialsProvided]: (state) =>
    state.merge({ isAuthenticationDone: true }),

  [refreshAccountsMfaFormSubmitResponse]: {
    next(state, { payload: institutionLoginId }) {
      if (state.institutionLogin && state.institutionLogin.id === institutionLoginId) {
        return state.merge({ isAuthenticationDone: true });
      }
      return state;
    },
  },

  [combineActions(upsertAccountsSuccess, upsertAccountsFailure)]:
    (state) => state.merge({
      isAccountUpsertingDone: true,
      isSubmitting: false,
    }),

}, mkInstitutionLoginUpserterStore());

export const REDUCER_KEY = 'upsertInstitutionLoginStore';
export default reducer;
